import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AuthShell } from "@/components/auth-shell";
import { SocialAuthButtons } from "@/components/social-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { azError } from "@/lib/az-errors";
import { formatPhone, normalizePhone, phoneToEmail, validatePhone } from "@/lib/username";

export const Route = createFileRoute("/qeydiyyat")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Qeydiyyat — Albazar.az" },
      { name: "description", content: "Pulsuz hesab yaradın və elanlarınızı dərhal yerləşdirin." },
      { property: "og:title", content: "Qeydiyyat — Albazar.az" },
      { property: "og:description", content: "Pulsuz hesab yaradın." },
    
      { property: "og:url", content: "https://albazar.az/qeydiyyat" },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: "https://albazar.az/qeydiyyat" }]
  }),
  component: SignUp,
});

function SignUp() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const problem = validatePhone(form.phone);
    if (problem) {
      toast.error(problem);
      return;
    }
    if (form.password.length < 6) {
      toast.error("Şifrə ən azı 6 simvol olmalıdır");
      return;
    }
    setBusy(true);
    const phone = normalizePhone(form.phone);
    const email = phoneToEmail(phone);
    const { data, error } = await supabase.auth.signUp({
      email,
      password: form.password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: { username: `p${phone}`, full_name: formatPhone(phone), phone: `+${phone}` },
      },
    });
    setBusy(false);
    if (error) {
      toast.error(azError(error));
      return;
    }
    if (!data.session) {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: form.password,
      });
      if (signInError) {
        toast.error(azError(signInError));
        return;
      }
    }
    toast.success("Hesabınız yaradıldı!");
    navigate({ to: "/profil", replace: true });
  };

  return (
    <>
    <AuthShell
      title="Qeydiyyat"
      subtitle="Google və ya telefon nömrəsi ilə 1 dəqiqəyə hesab yaradın"
      footer={
        <>
          Artıq hesabınız var?{" "}
          <Link to="/giris" className="font-semibold text-primary">Daxil olun</Link>
        </>
      }
    >
      <SocialAuthButtons />

      <form id="auth-form" className="space-y-4" onSubmit={submit}>
        <div>
          <Label htmlFor="phone">Telefon nömrəsi</Label>
          <Input
            id="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            required
            value={form.phone}
            onChange={set("phone")}
            className="mt-2 h-11 rounded-2xl"
            placeholder="050 123 45 67"
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            Giriş üçün bu nömrədən istifadə edəcəksiniz.
          </p>
        </div>
        <div>
          <Label htmlFor="pw">Şifrə</Label>
          <Input
            id="pw"
            type="password"
            autoComplete="new-password"
            required
            value={form.password}
            onChange={set("password")}
            className="mt-2 h-11 rounded-2xl"
            placeholder="••••••••"
          />
        </div>
        <Button type="submit" disabled={!hydrated || busy} className="h-12 w-full rounded-2xl text-base">
          {busy ? "Gözləyin..." : "Qeydiyyatdan keç"}
        </Button>
      </form>
    </AuthShell>
      {hydrated && form.phone.trim() && (
        <div className="fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+4.5rem)] z-[45] px-4 md:hidden">
          <Button type="submit" form="auth-form" disabled={busy} className=" h-12 w-full rounded-2xl text-base shadow-elevated">
            {busy ? "Gözləyin..." : "Qeydiyyatdan keç"}
          </Button>
        </div>
      )}
    </>
  );
}

